import { useEffect, useMemo, useState } from "react";

import { changelog } from "changelog";
import Modal from "components/dialog";
import Iconify from "components/iconify";
import { APP_VERSION } from "config/appVersion";
import { useTranslation } from "i18n";
import { appLanguages } from "utils/Currency&Language";

import "./style.css";

const COOKIE_NAME = "satyam-whats-new-version";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

const getCookie = (name) => {
  const value = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`));

  return value?.split("=")[1];
};

const setCookie = (name, value) => {
  document.cookie = `${name}=${value}; max-age=${COOKIE_MAX_AGE}; path=/; SameSite=Lax`;
};

const WhatsNewModal = () => {
  const { language, locale, setLanguage, t } = useTranslation();
  const [open, setOpen] = useState(false);

  const release = useMemo(() => changelog[APP_VERSION], []);
  const releaseCopy = release?.[locale] || release?.en;

  useEffect(() => {
    if (!release) return;

    const seenVersion = getCookie(COOKIE_NAME);
    setOpen(seenVersion !== APP_VERSION);
  }, [release]);

  const handleClose = () => {
    setCookie(COOKIE_NAME, APP_VERSION);
    setOpen(false);
  };

  if (!release || !releaseCopy) return null;

  return (
    <Modal open={open} onClose={handleClose}>
      <div className="whats-new-modal">
        <header className="whats-new-modal__head">
          <div>
            <p className="whats-new-modal__eyebrow">
              {t("whatsNew.version", { version: APP_VERSION })}
            </p>
            <h2>{t("whatsNew.title")}</h2>
          </div>
          <div className="whats-new-modal__actions">
            <div
              className="whats-new-modal__language-options"
              aria-label={t("whatsNew.languageTitle")}
            >
              {appLanguages.map((option) => {
                const isActive = option.locale === language?.locale;

                return (
                  <button
                    aria-label={t(option.labelKey)}
                    className={`whats-new-modal__language-option${
                      isActive ? " is-active" : ""
                    }`}
                    key={option.locale}
                    type="button"
                    onClick={() => setLanguage(option)}
                  >
                    <Iconify icon={`circle-flags:${option.code?.toLowerCase()}`} />
                    <span>{option.locale.toUpperCase()}</span>
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              className="whats-new-modal__close"
              onClick={handleClose}
              aria-label={t("common.close")}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M18.3 5.71a1 1 0 00-1.41 0L12 10.59 7.11 5.7A1 1 0 105.7 7.11L10.59 12 5.7 16.89a1 1 0 101.41 1.41L12 13.41l4.89 4.89a1 1 0 001.41-1.41L13.41 12l4.89-4.89a1 1 0 000-1.4z" />
              </svg>
            </button>
          </div>
        </header>

        <ul className="whats-new-modal__list">
          {releaseCopy.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>

        <button
          type="button"
          className="whats-new-modal__cta"
          onClick={handleClose}
        >
          {t("whatsNew.cta")}
        </button>
      </div>
    </Modal>
  );
};

export default WhatsNewModal;
